import React from 'react';

export default function EventDetailsTimetablePreview({ table, view }) {
  const {
    DEFAULT_TITLE_STYLE,
    adjustmentsMap,
    applyRowAdjustments,
    buildHeaderGroups,
    buildPreviewColumns,
    buildPreviewRows,
    getDefaultColumnAlign,
    hoveredPreviewRowId,
    memoMap,
    nowTimetableRowId,
    pickColumnWidths,
    pickRowHeights,
    pickTitleStyle,
    pinnedPreviewRowIds,
    runtimeData,
    setHoveredPreviewRowId,
    setPinnedPreviewRowIds,
  } = view;

    if (!table) return null;
    const data = table?.data || {};
    const columns = buildPreviewColumns(data);
    const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
    const columnWidths = pickColumnWidths(data, columns);
    const rowHeights = pickRowHeights(data, rows);
    const titleStyle = pickTitleStyle(data);
    const headerGroups = buildHeaderGroups(columns);

    const colGroup =
      columns.length > 0 ? (
        <colgroup>
          {columns.map((c) => {
            const w = columnWidths[c.id];
            const width = typeof w === 'number' && w > 0 ? `${w}px` : null;
            return <col key={c.id} style={width ? { width, minWidth: width } : undefined} />;
          })}
        </colgroup>
      ) : null;

    const header = (
      <tr className="timetable-title-row">
        {headerGroups.map((g, idx) => (
          <th
            key={`header-${idx}`}
            colSpan={g.span}
            style={{
              background: titleStyle.bgColor || DEFAULT_TITLE_STYLE.bgColor,
              color: titleStyle.textColor || DEFAULT_TITLE_STYLE.textColor,
              fontWeight: titleStyle.bold ? 700 : 600,
              fontSize: `${titleStyle.fontSize || DEFAULT_TITLE_STYLE.fontSize}px`,
              textAlign: g.align || 'left',
              height: titleStyle.height ? `${titleStyle.height}px` : undefined,
            }}
          >
            {g.label || ''}
          </th>
        ))}
      </tr>
    );

    const announcements = Array.isArray(runtimeData?.announcements) ? runtimeData.announcements : [];
    const body = [];
    rows.forEach((r) => {
      const rowHeight = rowHeights[r.id];
      const isPinned = pinnedPreviewRowIds.has(r.id);
      const isHover = hoveredPreviewRowId === r.id;
      const isNow = nowTimetableRowId === r.id;
      const rowClass = [
        isPinned ? 'is-active' : '',
        isHover ? 'is-hover' : '',
        isNow ? 'is-now' : '',
      ]
        .filter(Boolean)
        .join(' ');
      const memo = memoMap.get(String(r.id));
      const hasAdjustedTime =
        r.adjustedOffsetMinutes && r.adjustedStartTime && String(r.startTime || '').trim();
      const displayStartTime = hasAdjustedTime ? r.adjustedStartTime : r.startTime || '';

      body.push(
        <tr
          key={r.id}
          className={rowClass}
          style={rowHeight ? { height: `${rowHeight}px` } : undefined}
          onMouseEnter={() => setHoveredPreviewRowId(r.id)}
          onMouseLeave={() => setHoveredPreviewRowId((prev) => (prev === r.id ? null : prev))}
          onClick={() =>
            setPinnedPreviewRowIds((prev) => {
              const next = new Set(prev);
              if (next.has(r.id)) next.delete(r.id);
              else next.add(r.id);
              return next;
            })
          }
        >
          {columns.map((c) => {
            const cell = (r.cells && r.cells[c.id]) || {};
            if (cell.mergeParentId) return null;
            const spanProps = {
              rowSpan: cell.rowSpan || 1,
              colSpan: cell.colSpan || 1,
            };
            let value = '';
            if (c.id === 'startTime') {
              if (hasAdjustedTime) {
                const mode = r.adjustedOffsetMinutes > 0 ? 'is-push' : 'is-pull';
                value = (
                  <div className={`tt-time-adjusted ${mode}`}>
                    <span className="tt-time-original">{r.startTime || ''}</span>
                    <span className="tt-time-arrow">→</span>
                    <span className="tt-time-new">{displayStartTime}</span>
                  </div>
                );
              } else {
                value = displayStartTime || '';
              }
            } else if (c.id === 'duration') value = r.duration || '';
            else if (c.id === 'item') value = r.item || '';
            else value = cell.value || '';
            const bg = cell.color || r.rowColor || '';
            const txt = cell.textColor || r.rowTextColor || '';
            const align = c.align || getDefaultColumnAlign(c.id);
            return (
              <td
                key={`${r.id}-${c.id}`}
                {...spanProps}
                className={bg ? 'has-bg' : ''}
                style={{
                  textAlign: align,
                  background: bg || undefined,
                  color: txt || undefined,
                  fontWeight: cell.bold ? 700 : undefined,
                  fontSize: cell.fontSize ? `${cell.fontSize}px` : undefined,
                  whiteSpace: 'pre-wrap',
                  verticalAlign: 'middle',
                  padding: '8px 10px',
                }}
              >
                {value}
              </td>
            );
          })}
        </tr>,
      );

      if (memo?.content) {
        const memoVisibility = memo.visibility || 'all';
        body.push(
          <tr key={`${r.id}-memo`} className="timetable-row-memo">
            <td colSpan={columns.length} className="timetable-row-memo-cell">
              <div className="timetable-row-memo-content">
                <span className="timetable-row-memo-label">メモ</span>
                {memoVisibility === 'admin' ? (
                  <span className="timetable-row-memo-badge">管理者</span>
                ) : null}
                <span className="timetable-row-memo-text">{memo.content}</span>
              </div>
            </td>
          </tr>,
        );
      }
    });

    return (
      <div className="timetable-preview-wrap timetable-preview-print">
        {announcements.length ? (
          <div className="timetable-runtime-banner">
            {announcements.map((notice) => {
              const visibility = String(notice?.visibility || '').toLowerCase();
              const isAdminOnly = visibility === 'admin';
              return (
                <div key={notice.id || notice.start_at} className="timetable-runtime-banner__item">
                  <div className="timetable-runtime-banner__header">
                    <span className="timetable-runtime-banner__title">お知らせ</span>
                    {isAdminOnly ? (
                      <span className="timetable-runtime-banner__badge">管理者のみ</span>
                    ) : null}
                  </div>
                  <div className="timetable-runtime-banner__content">{notice.content}</div>
                </div>
              );
            })}
          </div>
        ) : null}
        <div className="timetable-preview-table-scroll">
          <table className="timetable-preview-table">
            {colGroup}
            <thead>{header}</thead>
            <tbody>{body}</tbody>
          </table>
        </div>
        <div className="timetable-preview-note">
          <div className="note-label">備考</div>
          <div className="note-body">{data.note ? data.note.split('\\n').map((line, idx) => <div key={idx}>{line}</div>) : '—'}</div>
        </div>
      </div>
    );
}
